import type { AdminScreen } from '../../lib/location';
import type { NodeId } from '../../lib/types';
import type { SliceCreator } from '../state';

/**
 * Chrome state: what mode the app is in and what the frame around the map looks like.
 *
 * THE SPLIT THAT MATTERS IS BY LIFETIME, not by subject, and it is already made for us by the
 * two localStorage caches (see `store/persistence.ts`). Five of these are DURABLE prefs shared
 * by every user of the browser and never expiring — theme, sidebar open, sidebar width, edit
 * mode, cross-links — because resetting somebody's dark mode overnight reads as a bug. The
 * rest are session state: `linkArmed` is a tool waiting for its second click, `undoLabel` is a
 * question about the undo stack, `aiDocMode` belongs to the day-scoped view record.
 *
 * `undoLabel` is a LABEL rather than the stack, kept exactly as the bridge carries it: "can I
 * undo" and "undo what" are the same question for every consumer, and handing over the stack
 * would invite reading past the top of it.
 */
export interface UiSlice {
  /** The one global edit toggle. Gated by `canEnterEdit()` wherever it is set. */
  editing: boolean;
  isLight: boolean;
  sidebarOpen: boolean;
  sidebarWidth: number;
  /** Whether dotted cross-links are drawn. A preference, not map content. */
  showLinks: boolean;
  /** The minimap is drawn (V0.53.4). A durable pref beside the theme; `features/minimap` reads it. */
  minimapOn: boolean;
  /** The minimap keeps its bright level instead of fading at rest (V0.54). Durable, like `minimapOn`. */
  minimapBright: boolean;
  /** The Link tool is armed and waiting for a second node. */
  linkArmed: boolean;
  /** AI-doc tools are active: the toolbar, the extra popup fields, the status filter. */
  aiDocMode: boolean;
  /** The clearance heatmap (V0.39): nodes take their bucket colour. Day-scoped like aiDocMode. */
  heatmapOn: boolean;
  /** The documentation rows are pinned open (V0.42.2): the second input to `docsExpanded`. */
  docsPinned: boolean;
  /** What the next undo would undo, or null when there is nothing. */
  undoLabel: string | null;
  /** Which admin surface is open, or null (plan 1.6) — the URL's `/admin/{screen}` segment. */
  adminScreen: AdminScreen | null;
  /**
   * Ids of top-bar controls currently living in a "…" overflow menu rather than the bar
   * (plan 1.3). Written by the fit controller through the app state and mirrored here like
   * everything else; compared by CONTENT in the setter because the mirror re-states it on
   * every sync (the same argument `filters.ts` makes for `chatHighlight`).
   */
  topbarOverflow: readonly string[];

  /**
   * Transient pointer state, held here rather than in a component for one reason: it decides
   * a CLASS on exactly one node, and a node has to be able to subscribe to its own answer.
   *
   * The vanilla version wrote these classes straight onto the element — `nd.classList.add
   * ('dragging')`, `.drop-target`, `.linksel` — which React would silently undo on its next
   * render of that node. Making them state is not ceremony; it is the only way the two facts
   * (what the pointer is doing, what the node looks like) can stay the same fact.
   */
  dragNodeId: NodeId | null;
  /** The node a drop would reparent onto, highlighted while the pointer is over it. */
  dropTargetId: NodeId | null;
  /** The Link tool's first click, waiting for its second. */
  linkFromId: NodeId | null;
  /** The node whose label is being edited in place. */
  renamingId: NodeId | null;
  /**
   * Ctrl (or Cmd) is held, so a click adds a child rather than dragging.
   *
   * Held here rather than watched by the map, because the key handler that sets it also has
   * to know whether a popup is open and whether the Link tool is armed — three facts that
   * belong to the chrome, not to the stage.
   */
  ctrlAdd: boolean;
  /**
   * Where a node currently IS, when it is not on the map (V0.47).
   *
   * Opening a popup lifts the node off the map and lands it in a seat at the top of the popup;
   * opening a page from there moves it on to the bubble the document view draws; each close
   * is the trip back. During those trips exactly one node is somewhere other than its place,
   * and three renderers need to know — the map node (hide it), the popup's seat (draw it
   * empty until the node arrives) and the band's bubble (likewise). One fact, three readers,
   * for the same reason `dragNodeId` above is state and not a class the pointer handler adds:
   * a DOM write is undone by the next render, state is not.
   *
   * `null` means everything is in its place. `'air'` is the crossing itself. Under
   * `prefers-reduced-motion` this is NEVER set — the whole regression suite runs that way and
   * sees today's visibility exactly.
   */
  perch: { id: NodeId; at: 'seat' | 'bubble' | 'air' } | null;

  setEditing: (on: boolean) => void;
  setLight: (on: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarWidth: (px: number) => void;
  setShowLinks: (on: boolean) => void;
  setMinimapOn: (on: boolean) => void;
  setMinimapBright: (on: boolean) => void;
  setLinkArmed: (on: boolean) => void;
  setAiDocMode: (on: boolean) => void;
  setHeatmapOn: (on: boolean) => void;
  setDocsPinned: (on: boolean) => void;
  setUndoLabel: (label: string | null) => void;
  setAdminScreen: (screen: AdminScreen | null) => void;
  setTopbarOverflow: (ids: readonly string[]) => void;
  setDragNodeId: (id: NodeId | null) => void;
  setDropTargetId: (id: NodeId | null) => void;
  setLinkFromId: (id: NodeId | null) => void;
  setRenamingId: (id: NodeId | null) => void;
  setCtrlAdd: (on: boolean) => void;
  setPerch: (p: UiSlice['perch']) => void;
}

/** The sidebar's drag handle stops here, same as the vanilla `load()` clamp. */
export const MIN_SIDEBAR = 200;
export const MAX_SIDEBAR = 560;
export const DEFAULT_SIDEBAR = 260;

/**
 * Every setter below refuses a write that changes nothing, and that is a MAP requirement
 * rather than tidiness.
 *
 * `store/fromApp.ts` re-states all of these on every `sync()` — which is every vanilla
 * `render()` — and Zustand notifies on every `setState` whether or not the value moved. With
 * 147 map nodes each holding a selector subscription, a setter that always writes costs 147
 * selector evaluations per call for nothing. Refusing the no-op turns a sync from sixteen
 * notifications into one or two.
 */
export const createUiSlice: SliceCreator<UiSlice> = (set, get) => {
  /** Write only when the value actually moved. See the note above. */
  const put = <K extends keyof UiSlice>(key: K, value: UiSlice[K]) => {
    if (get()[key] === value) return;
    set({ [key]: value } as Partial<UiSlice>);
  };
  return {
    editing: false,
    isLight: false,
    sidebarOpen: true,
    sidebarWidth: DEFAULT_SIDEBAR,
    showLinks: false,
    minimapOn: true,
    minimapBright: false,
    linkArmed: false,
    aiDocMode: false,
    heatmapOn: false,
    docsPinned: false,
    undoLabel: null,
    adminScreen: null,
    topbarOverflow: [],
    dragNodeId: null,
    dropTargetId: null,
    linkFromId: null,
    renamingId: null,
    ctrlAdd: false,
    perch: null,

    setEditing: (on) => put('editing', on),
    setLight: (on) => put('isLight', on),
    setSidebarOpen: (open) => put('sidebarOpen', open),
    setSidebarWidth: (px) =>
      put('sidebarWidth', Math.max(MIN_SIDEBAR, Math.min(MAX_SIDEBAR, px || DEFAULT_SIDEBAR))),
    setShowLinks: (on) => put('showLinks', on),
    setMinimapOn: (on) => put('minimapOn', on),
    setMinimapBright: (on) => put('minimapBright', on),
    setLinkArmed: (on) => put('linkArmed', on),
    setAiDocMode: (on) => put('aiDocMode', on),
    setHeatmapOn: (on) => put('heatmapOn', on),
    setDocsPinned: (on) => put('docsPinned', on),
    setUndoLabel: (label) => put('undoLabel', label),
    setAdminScreen: (screen) => put('adminScreen', screen),
    setTopbarOverflow: (ids) => {
      const prev = get().topbarOverflow;
      if (prev.length === ids.length && prev.every((id, i) => id === ids[i])) return;
      set({ topbarOverflow: ids });
    },
    setDragNodeId: (id) => put('dragNodeId', id),
    setDropTargetId: (id) => put('dropTargetId', id),
    setLinkFromId: (id) => put('linkFromId', id),
    setRenamingId: (id) => put('renamingId', id),
    setCtrlAdd: (on) => put('ctrlAdd', on),
    // Compared by content: the flight re-states `air` on every frame's bookkeeping path, and
    // 147 node subscriptions must not wake for a value that did not move.
    setPerch: (p) => {
      const prev = get().perch;
      if (prev === p || (prev && p && prev.id === p.id && prev.at === p.at)) return;
      set({ perch: p });
    },
  };
};
